import React, { useState, useRef, useEffect, useCallback } from "react";
import { IoClose } from "react-icons/io5";
import { positionRanks } from "../../../utilities/PostitionRanks";


// Define the shape of each option object
export interface DropdownOption {
    label: string;
    value: string | number;
    roles: string[];
}

const Close = IoClose as any;

interface MultiSelectDropdownProps {
    label?: string;
    name: string;
    values: (string | number)[]; // The selected values
    onChange: (values: (string | number)[]) => void; // Pass the array of selected values
    options: DropdownOption[];
    error?: string;
    isSearchable?: boolean;
    isWhiteBg?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
    label,
    name,
    values,
    onChange,
    options,
    error,
    isSearchable = true,
    isWhiteBg,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [hoveredIndex, setHoveredIndex] = useState(0);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Filter options based on search term, excluding already selected ones
    // const filteredOptions = options.filter(option =>
    //     !values.includes(option.value) &&
    //     option.label.toLowerCase().includes(searchTerm.toLowerCase())
    // );

    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Reset state when dropdown is closed
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm("");
            setHoveredIndex(0);
        }
    }, [isOpen]);

    // Scroll to hovered item
    useEffect(() => {
        if (isOpen && listRef.current && hoveredIndex >= 0) {
            const itemElement = listRef.current.querySelector(`[data-index="${hoveredIndex}"]`);
            if (itemElement) {
                itemElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [hoveredIndex, isOpen]);

    // Scroll to bottom of container when dropdown opens or when typing
    useEffect(() => {
        if (isOpen && containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [isOpen, searchTerm]);

    // Toggle selection
    const handleSelect = useCallback((option: DropdownOption) => {
        const newValues = values.includes(option.value)
            ? values.filter(v => v !== option.value)
            : [...values, option.value];
        onChange(newValues);
        setSearchTerm(""); // Clear search after selection
    }, [values, onChange]);

    // Remove a selected item
    const handleRemove = (valueToRemove: string | number) => {
        const newValues = values.filter(v => v !== valueToRemove);
        onChange(newValues);
    };

    // Handle keyboard navigation and typing
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === ' ' && searchTerm === '' && !isOpen) {
            e.preventDefault();
            setIsOpen(true);
            return;
        }
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHoveredIndex(prev => (prev + 1) % (filteredOptions.length || 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHoveredIndex(prev => (prev - 1 + (filteredOptions.length || 1)) % (filteredOptions.length || 1));
                break;
            case 'Enter':
            case 'Tab':
                if (filteredOptions[hoveredIndex]) {
                    e.preventDefault();
                    handleSelect(filteredOptions[hoveredIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;
            case 'Backspace':
                if (searchTerm) {
                    setSearchTerm(prev => prev.slice(0, -1));
                } else if (values.length > 0) {
                    handleRemove(values[values.length - 1]);
                }
                break;
            default:
                if (isSearchable && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
                    setSearchTerm(prev => prev + e.key);
                    setHoveredIndex(0);
                }
                break;
        }
    }, [isOpen, filteredOptions, hoveredIndex, handleSelect, searchTerm, values, isSearchable]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const selectedOptions = options.filter(option => values.includes(option.value));
    const placeholderText = `Select ${(label || name).toLowerCase()}...`;

    const MAX_VISIBLE = 2;
    const visibleSelected = selectedOptions.slice(0, MAX_VISIBLE);
    const remainingCount = selectedOptions.length - MAX_VISIBLE;


    return (
        <div className="relative w-full" ref={dropdownRef}>
            {(label) && <label className="block text-[#212121] font-medium text-[13px] 2xl:text-[16px] mb-1">{label}</label>}
            <div
                ref={containerRef}
                className={`w-full form-select no-scrollbar rounded-md flex items-center flex-wrap pt-1 gap-1 transition-colors duration-200 ${isWhiteBg ? '!bg-white' : 'bg-gray-50'} cursor-text max-h-40 overflow-y-auto ${error ? 'border-red-500' : 'border-gray-300'
                    } ${isOpen ? 'border-indigo-500 ring-1 ring-indigo-500' : 'hover:border-gray-400'}`}
                onClick={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
                tabIndex={0}
            >
                {/* Selected Chips */}
                {visibleSelected.map((option, index) => {

                    const chipStyle =
                        index === 0
                            ? {
                                backgroundColor: "#FFEDD4",
                                border: "1px solid #FF8904",
                                color: "#101828",
                            }
                            : {
                                backgroundColor: "#DCFCE7",
                                border: "1px solid #05DF72",
                                color: "#101828",
                            };

                    const chipClass =
                        index === 0
                            ? "m-0.5 inline-flex items-center gap-1 h-[20px] rounded-[4.96px] opacity-100 font-[Poppins] font-normal text-[16px] leading-[22px] tracking-[0px] bg-[#FFEDD4] border-t-[#FF8904] text-[#101828]"
                            : "m-0.5 inline-flex items-center gap-1 h-[20px] rounded-[4.96px] opacity-100 font-[Poppins] font-normal text-[16px] leading-[22px] tracking-[0px] bg-[#DCFCE7]  border-t-[#05DF72] text-[#101828]";

                    return (
                        <span key={option.value} style={chipStyle} className={`${chipClass} max-w-[67px] `}>
                            <div className="ps-2 truncate overflow-hidden whitespace-nowrap text-[11px]">
                                {option.label}
                            </div>

                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemove(option.value);
                                }}
                                className="w-4 h-4 me-1 flex items-center text-[13px] justify-center rounded-full hover:bg-black/10"
                            >
                                <Close />{""}
                            </button>
                        </span>
                    );

                })}

                {/* +N more */}
                {remainingCount > 0 && (
                    <span className="m-0.5 text-[12px] text-[#747474] font-medium rounded-full inline-flex items-center select-none">
                        +{remainingCount} more
                    </span>
                )}


                <div className="flex-grow relative">
                    <span className="text-sm invisible whitespace-pre text-gray-800">{searchTerm || (values.length === 0 ? placeholderText : '')}</span>
                    <div className="absolute inset-0 flex items-center">
                        <span className="text-sm text-gray-800">{searchTerm}</span>
                        {isOpen && (
                            <span className="animate-blink w-px h-4 bg-gray-800 ml-px"></span>
                        )}
                    </div>
                    {values.length === 0 && !searchTerm && (
                        <span className="absolute inset-0 text-[13px] 2xl:text-[16px] flex items-center ps-2">{placeholderText}</span>
                    )}
                </div>
            </div>
            {isOpen && (
                <ul
                    ref={listRef}
                    className="p-0 absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                >
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => {
                            const highestRole =
                                option.roles.length > 0
                                    ? option.roles.reduce((prev, current) =>
                                        positionRanks[prev] > positionRanks[current] ? prev : current
                                    )
                                    : null;


                            return (
                                <li
                                    key={option.value}
                                    data-index={index}
                                    onClick={() => handleSelect(option)}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    className="px-1.5 overflow-x-hidden py-2 flex cursor-pointer w-full"
                                    style={{ alignItems: "start" }}
                                >
                                    {/* LEFT: Checkbox + Name */}
                                    <div className="flex gap-1.5 w-[60%] pr-2" style={{ alignItems: "start" }}>
                                        <span
                                            className={`min-w-5 min-h-5 flex items-center justify-center rounded border ${selectedOptions.some(o => o.value === option.value)
                                                ? 'bg-[#7E57C2] border-[#7E57C2]'
                                                : 'border-gray-300 bg-white'
                                                }`}
                                        >
                                            {selectedOptions.some(o => o.value === option.value) && (
                                                <svg
                                                    className="w-3 h-3 text-white"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="3"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </span>

                                        <span className="text-gray-900 text-xs font-medium break-words max-w-[90px]">
                                            {option.label}
                                        </span>
                                    </div>

                                    {/* RIGHT: Role */}
                                    <span className="text-gray-500 text-xs font-medium w-[40%] ps-2 text-end break-words">
                                        {highestRole || "-"}
                                    </span>

                                </li>
                            );
                        })
                    ) : (
                        <li className="px-3 py-2 text-gray-500 text-sm">No options found</li>
                    )}
                </ul>

            )}
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            <style>{`
                @keyframes blink {
                    50% { opacity: 0; }
                }
                .animate-blink {
                    animation: blink 1s step-end infinite;
                }
            `}</style>
        </div>
    );
};

export default MultiSelectDropdown;
